// src/pages/LearnPage.tsx

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchTopicReviewById, submitReview, searchImagesForWord } from '../api/api';
import { VocabularyWord, Topic, VocabularyReview } from '../types';
import { ReactComponent as SpeakerIcon } from '../assets/speaker-icon.svg';
import EmptyState from '../components/EmptyState';

function LearnPage() {
  // --- 1. TẤT CẢ HOOKS ĐƯỢC KHAI BÁO Ở ĐẦU ---
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();

  const [vocabs, setVocabs] = useState<VocabularyReview[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState<string[]>([]);
  const [areImagesLoading, setAreImagesLoading] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Lấy ra từ hiện tại một cách an toàn
  const currentWord = vocabs[currentWordIndex];

  // --- 2. LOGIC ĐƯỢC XỬ LÝ TRONG USEEFFECT ---

  // useEffect để tải dữ liệu từ vựng của topic
  useEffect(() => {
    if (topicId) {
      const getVocabsData = async () => {
        setLoading(true);
        try {
          const topicData = await fetchTopicReviewById(topicId);
          setVocabs(topicData || []);
        } catch (error) {
          console.error("Failed to fetch topic data:", error);
          setVocabs([]);
        } finally {
          setLoading(false);
        }
      };
      getVocabsData();
    }
  }, [topicId]);

  // useEffect để tìm ảnh minh họa
  useEffect(() => {
    // Điều kiện nằm bên trong hook
    if (currentWord) {
      const findImages = async () => {
        setAreImagesLoading(true);
        try {
          // const searchQuery = `illustration for ${currentWord.word}`;
          const imageUrls = await searchImagesForWord(currentWord.word, currentWord.id);
          setImages(imageUrls.slice(0, 10));
        } catch (error) {
          console.error("Failed to search for images:", error);
          setImages([]);
        } finally {
          setAreImagesLoading(false);
        }
      };
      findImages();
    }
    // // Nếu lật thẻ hoặc chuyển từ, xóa ảnh cũ đi
    // if (isFlipped) {
    //   setImages([]);
    // }
  }, [currentWord]);

  useEffect(() => {
    const loadVoices = () => {
      setVoices(window.speechSynthesis.getVoices());
    };

    // API getVoices() đôi khi tải bất đồng bộ
    window.speechSynthesis.onvoiceschanged = loadVoices;
    loadVoices(); // Gọi lần đầu phòng trường hợp đã có sẵn

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // --- 3. CÁC HÀM XỬ LÝ SỰ KIỆN ---

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentWordIndex(prev => (prev + 1) % vocabs.length);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setCurrentWordIndex(prev => (prev - 1 + vocabs.length) % vocabs.length);
  };

  const handleFlipAndMarkLearned = async () => {
    // Chỉ gọi API một lần khi lật sang mặt sau
    if (!isFlipped) {
      try {
        await submitReview(currentWord.id, "", 1);
      } catch (error) {
        console.error("Failed to mark as learned:", error);
      }
    }
    setIsFlipped(!isFlipped);
  };

  const handleSpeak = (event: React.MouseEvent) => {
    event.stopPropagation();
    if ('speechSynthesis' in window && currentWord) {
      const utterance = new SpeechSynthesisUtterance(currentWord.word);
      
      // Tìm một giọng nữ tiếng Anh (en-US) trong danh sách
      const femaleVoice = voices.find(voice => 
        voice.lang === 'en-US' && 
        (voice.name.includes('Female') || voice.name.includes('Zira') || voice.name.includes('Samantha'))
      );

      // Nếu tìm thấy, sử dụng giọng đó
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    } else {
      alert("Trình duyệt của bạn không hỗ trợ tính năng phát âm.");
    }
  };

  // --- 4. CÁC LỆNH RETURN SỚM (GUARD CLAUSES) ---
  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading lesson...</div>;
  }

  if (!vocabs || vocabs.length === 0) {
    return (
       <EmptyState
        title="Nothing to Learn"
        message="Sorry, no vocabularies found in this topic."
        action={
          <button onClick={() => navigate(-1)} className="btn btn-primary">
            ‹ Go Back
          </button>
        }
      />
    );
  }

  return (
    <div className="learn-container">
      {/* Header của trang học */}
      <header className="page-header">
        <button onClick={() => navigate(-1)} className="btn-back">‹ Back</button>
        <div className="topic-title">
          {/* <p>Topic</p> */}
          <h2>{currentWord.topicName}</h2>
        </div>
      </header>

      {/* Thanh tiến trình */}
      <div className="progress-bar-container">
        <div
          className="progress-bar"
          style={{ width: `${(currentWordIndex + 1) / vocabs.length * 100}%` }}
        ></div>
      </div>

      {/* Flashcard để học */}
      <main className="flashcard-area">
        <div className={`flashcard ${isFlipped ? 'is-flipped' : ''}`} onClick={handleFlipAndMarkLearned}>
          <div className="flashcard-inner">
            <div className="flashcard-face flashcard-front">
              {/* <div className="main-word">{currentWord.word}</div> */}
              <div className="word-container">
                <div className="main-word">{currentWord.word}</div>
                <button onClick={handleSpeak} className="speak-button">
                  <SpeakerIcon />
                </button>
              </div>
              <div className="word-type-ipa">
                <span className="word-type">{currentWord.type}</span>
                <span className="ipa">{currentWord.ipa}</span>
              </div>
              <span className="flip-indicator">Click to flip</span>
            </div>
            <div className="flashcard-face flashcard-back">
              <div className="definition">
                <h4>Definition</h4>
                <p>{currentWord.definition}</p>
              </div>
              <div className="example">
                <h4>Example</h4>
                <p>1: "{currentWord.example1}"</p>
                {currentWord.example2 && <p>2: "{currentWord.example2}"</p>}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer chứa nút điều hướng */}
      {/* <footer className="learn-footer">
        <button onClick={handlePrev} className="btn btn-nav">‹ Previous</button>
        <span className="progress-text">{currentWordIndex + 1} / {vocabs.length}</span>
        <button onClick={handleNext} className="btn btn-nav">Next ›</button>
      </footer> */}
      <footer className="page-footer">
        <button onClick={handlePrev} className="btn btn-secondary">‹ Previous</button>
        <span className="progress-text">{currentWordIndex + 1} / {vocabs.length}</span>
        <button onClick={handleNext} className="btn btn-secondary">Next ›</button>
      </footer>
      {/* --- KHU VỰC HIỂN THỊ HÌNH ẢNH MỚI --- */}
      <div className="image-results-container">
        <h4>Visual Context</h4>
        <p className="image-disclaimer">(Images are for reference only)</p>
        {areImagesLoading ? (
          <div className="loading-spinner-small"></div>
        ) : (
          <div className="image-grid">
            {images.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`${currentWord.word} example ${index + 1}`}
                // Thêm dòng onError này
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LearnPage;
